import React from "react";
import { BarChart3, Crown, Gem, Users } from "lucide-react";
import { deriveAirInsights } from "@/lib/deriveAirInsights";

const ICONS = {
  concentration: BarChart3,
  leader: Crown,
  "high-value": Gem,
  volume: Users,
};

export default function AirInsights({ mi }) {
  const result = deriveAirInsights(mi);
  // Keep the dashboard render-safe across a staggered frontend rollout. The
  // original insight builder exposed `conclusions`; the current contract uses
  // `insights`. A mixed bundle must degrade to the empty state, never take down
  // the whole dashboard with an undefined `.map()` call.
  const insights = Array.isArray(result?.insights)
    ? result.insights
    : (Array.isArray(result?.conclusions) ? result.conclusions : []);
  const hasInsights = Boolean(result?.ok && insights.length);

  return (
    <section className="space-y-3" data-testid="air-insights">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-500">
              Conclusions aviation
            </h2>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-lodge-mint/20 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-lodge-deep">
              <span className="h-1.5 w-1.5 rounded-full bg-lodge-mint" /> Lodge Market Data
            </span>
          </div>
          <p className="mt-1 text-xs text-lodge-ardoise">Données agrégées · {mi?.period?.label || "Période disponible"}</p>
        </div>
      </div>

      {hasInsights ? (
        <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">
          {insights.map((insight) => {
            const Icon = ICONS[insight.id] || BarChart3;
            return (
              <article key={insight.id} data-testid={`air-insight-${insight.id}`} className="min-w-0 rounded-2xl border border-lodge-hairline bg-white p-5 shadow-lodge-sm sm:p-6">
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-lodge-mint/20 text-lodge-deep"><Icon size={17} /></span>
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#3E7256]">{insight.eyebrow}</p>
                    <h3 className="mt-1 font-heading text-base font-semibold text-[#1E3D2B]">{insight.title}</h3>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-600">{insight.statement}</p>
                <dl className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {(Array.isArray(insight.evidence) ? insight.evidence : []).map((item) => (
                    <div key={item.label} className="min-w-0 rounded-lg bg-lodge-ivory px-3 py-2">
                      <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{item.label}</dt>
                      <dd className="mt-0.5 whitespace-nowrap font-mono text-sm font-semibold tabular-nums text-[#1E3D2B]">{item.value}</dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-4 border-t border-slate-100 pt-3 text-xs leading-relaxed text-slate-500">
                  <span className="font-semibold text-[#3E7256]">Implication — </span>{insight.implication}
                </p>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="rounded-2xl border border-lodge-hairline bg-white p-6 text-sm text-slate-500" data-testid="air-insights-empty">
          Données insuffisantes pour produire des conclusions aviation fiables.
        </div>
      )}

      <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-800" data-testid="air-insights-disclaimer">
        {result?.disclaimer}
      </p>
    </section>
  );
}
